import classNames from 'clsx';
import React, { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EAccessRights } from 'reactApp/components/SharingWindow/Sharing.types';
import SharingAddUserAccessRights from 'reactApp/components/SharingWindow/SharingNew/AddUser/SharingAddUserAccessRights';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/SharingNew/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { type Contact, contactsSlice } from 'reactApp/modules/contacts/contacts.module';
import { contactsSelectors } from 'reactApp/modules/contacts/contacts.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { Avatar } from 'reactApp/ui/Avatar/Avatar';
import { Button } from 'reactApp/ui/Button/Button';
import { InputSuggest } from 'reactApp/ui/InputSuggest/InputSuggest';
import { EInputSuggestTheme } from 'reactApp/ui/InputSuggest/InputSuggest.types';

import styles from './SharingNewAddUser.css';
import type { IProps } from './SharingNewAddUser.types';

// eslint-disable-next-line max-lines-per-function
export const SharingNewAddUser = React.memo(function SharingNewAddUser(props: IProps): ReactElement {
    const {
        isPhone,
        isMounted,
        handleAddUser,
        isLoading,
        error,
        clearError,
        isReadOnly = false,
        acList,
        variant = SHARING_VARIANT.default,
    } = props;

    const dispatch = useDispatch();

    const contactSuggestions = useSelector(contactsSelectors.getContactSuggestions);
    const activeUserEmail = useSelector(UserSelectors.getEmail);
    const isBizUser = useSelector(UserSelectors.isBizUser);

    const [isButtonDisabled, setButtonDisabled] = useState<boolean>(true);
    const [currentEmail, setCurrentEmail] = useState<string>('');
    const [accessRights, setAccessRights] = useState(isReadOnly || isBizUser ? EAccessRights.READ_ONLY : EAccessRights.READ_WRITE);

    const isRedesign = variant !== SHARING_VARIANT.default;
    const isInputDisabled = isLoading || Boolean(isMounted);

    const filteredSuggestions = useMemo(() => {
        const excludeList: { email: string | null }[] = [...(acList ?? []), { email: activeUserEmail }];
        return contactSuggestions?.filter((suggest) => !excludeList?.find((item) => item.email === suggest.email)) ?? null;
    }, [acList, activeUserEmail, contactSuggestions]);

    const handleLoginChange = useCallback(
        (e, { value }) => {
            setCurrentEmail(value);
            setButtonDisabled(!value.length);
            clearError?.();
        },
        [clearError]
    );

    const renderItem = useCallback((user: Contact, width = 0): ReactElement | null => {
        if (!user) {
            return null;
        }

        return (
            <div className={styles.user} data-qa-email={user.email} style={{ width }}>
                <div className={styles.avatar}>
                    <Avatar email={user.email} size={36} />
                </div>
                <div className={styles.info}>
                    {user.name && <div className={styles.name}>{user.name}</div>}
                    <div className={styles.email}>{user.email}</div>
                </div>
            </div>
        );
    }, []);

    const onAddClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            handleAddUser({ currentEmail, accessRight: accessRights });
            setButtonDisabled(true);
            setCurrentEmail('');
        },
        [accessRights, currentEmail, handleAddUser]
    );

    const handleGetSuggestions = useCallback(
        (filter) => {
            dispatch(contactsSlice.actions.suggestRequest(filter));
        },
        [dispatch]
    );

    const action = useMemo(() => {
        if (isReadOnly) {
            return;
        }

        return (
            <div className={classNames(styles.dropdown, { [styles.tabs]: isRedesign })}>
                <SharingAddUserAccessRights
                    isInputDisabled={isInputDisabled}
                    redesign={isRedesign}
                    accessRights={accessRights}
                    setAccessRights={setAccessRights}
                />
            </div>
        );
    }, [isReadOnly, accessRights, isInputDisabled, isRedesign]);

    return (
        <form
            className={classNames(styles.root, {
                [styles.root_phone]: isPhone,
                [styles.tabs]: isRedesign,
                [styles.tabsError]: isRedesign && error,
            })}
        >
            <div className={classNames(styles.inputRow, { [styles.inputRowRedesign]: isRedesign })}>
                <div className={styles.inputField}>
                    <InputSuggest
                        value={currentEmail}
                        placeholder={isRedesign ? 'Добавьте email пользователя' : 'Введите email'}
                        hasError={Boolean(error)}
                        suggestions={currentEmail ? filteredSuggestions : []}
                        getSuggestionItemId={(item) => item.email}
                        onChange={handleLoginChange}
                        onRequestSuggestions={handleGetSuggestions}
                        disabled={isInputDisabled}
                        focus={false}
                        action={isPhone ? undefined : action}
                        theme={isPhone ? EInputSuggestTheme.vk : undefined}
                        renderSuggestionItem={renderItem}
                        hideActionOnBlur={isRedesign}
                    />
                </div>
                <div className={styles.buttonWrapper}>
                    <div className={styles.action}>{isPhone ? action : undefined}</div>
                    <Button
                        theme={isPhone ? 'vk' : 'octavius'}
                        primary
                        middle
                        disabled={isInputDisabled || isButtonDisabled}
                        onClick={onAddClick}
                        className={classNames({ [styles.tabs]: isRedesign })}
                    >
                        Добавить
                    </Button>
                </div>
            </div>
            {Boolean(error) && <div className={styles.errorMessage} dangerouslySetInnerHTML={{ __html: error }} />}
        </form>
    );
});
